import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useDispatch, useSelector } from "react-redux";
import { fetchNewsAsync } from "../../store/slices/newsSlice";
import { fetchHomeAllMatches } from "../../store/slices/matchesSlice";
import commentary from "../../assets/images/commentary.png";
import MatchCard from "../common/MatchCard";
import cricket from "../../assets/images/menu-4.png";
import football from "../../assets/images/menu-1.png";
import tennis from "../../assets/images/menu-2.png";
import casino from "../../assets/images/menu-99998.png";
import upcomingEventsIcon from "../../assets/images/menu-upcoming.png";
import casinoPoint100 from "../../assets/images/casinopoint100.png";
import {
  BANNER_SLIDES,
  BANNER_SLIDES_QT,
  CASINO_PROVIDER_IMAGES,
  CASINO_PROVIDER_IMAGES_QT,
  THUMB_IMAGES,
  THUMB_IMAGES_QT,
} from "../../constants/casinoProviderImages";
import {
  navigateToCasinoGameLaunch,
  thumbItemHasPath,
} from "../../utils/casinoLaunchNavigation";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { Modal } from "react-bootstrap";

const CRICKET_EVENT_TYPE_ID = 4;
const FOOTBALL_EVENT_TYPE_ID = 1;
const TENNIS_EVENT_TYPE_ID = 2;
const MAX_MATCHES_HOME = 100;

const filterLiveBySport = (matches) =>
  (matches || []).filter((m) => m.in_play === 1).slice(0, MAX_MATCHES_HOME);

const filterUpcomingBySport = (matches) =>
  (matches || []).filter((m) => m.in_play !== 1).slice(0, MAX_MATCHES_HOME);

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth || {});
  const { matchesBySport } = useSelector((state) => state.matches);
  const { news } = useSelector((state) => state.news);

  const cricketAll = matchesBySport[String(CRICKET_EVENT_TYPE_ID)] || [];
  const footballAll = matchesBySport[String(FOOTBALL_EVENT_TYPE_ID)] || [];
  const tennisAll = matchesBySport[String(TENNIS_EVENT_TYPE_ID)] || [];

  // Load news once when the Home page mounts and short-poll home matches every 3s.
  useEffect(() => {
    // dispatch(fetchNewsAsync()).catch(() => {});

    // Initial home matches fetch
    // dispatch(fetchHomeAllMatches()).catch(() => {});

    // Short-pool: refresh home matches every 3 seconds
    const intervalId = setInterval(() => {
      dispatch(fetchHomeAllMatches()).catch(() => {});
    }, 3000);

    return () => clearInterval(intervalId);
  }, [dispatch]);

  const newsHeadingsText = useMemo(() => {
    const payload = news;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return items
      .map((x) => x?.heading)
      .filter(Boolean)
      .join("  •  ");
  }, [news]);

  const cricketMatches = useMemo(
    () => filterLiveBySport(cricketAll),
    [cricketAll],
  );
  const footballMatches = useMemo(
    () => filterLiveBySport(footballAll),
    [footballAll],
  );
  const tennisMatches = useMemo(
    () => filterLiveBySport(tennisAll),
    [tennisAll],
  );

  const upcomingCricketMatches = useMemo(
    () => filterUpcomingBySport(cricketAll),
    [cricketAll],
  );
  const upcomingFootballMatches = useMemo(
    () => filterUpcomingBySport(footballAll),
    [footballAll],
  );
  const upcomingTennisMatches = useMemo(
    () => filterUpcomingBySport(tennisAll),
    [tennisAll],
  );
  const hasCricket = cricketMatches.length > 0;
  const hasFootball = footballMatches.length > 0;
  const hasTennis = tennisMatches.length > 0;
  const hasAnyTodayMatches = hasCricket || hasFootball || hasTennis;
  const hasUpcomingCricket = upcomingCricketMatches.length > 0;
  const hasUpcomingFootball = upcomingFootballMatches.length > 0;
  const hasUpcomingTennis = upcomingTennisMatches.length > 0;
  const hasAnyUpcomingMatches =
    hasUpcomingCricket || hasUpcomingFootball || hasUpcomingTennis;

  const [dismissedConversionRateDialog, setDismissedConversionRateDialog] =
    useState(false);
  const [pathUrl, setPathUrl] = useState("");

  const casinoConversionRate = useAppSelector(
    (state) => state.casino?.casinoConversionRate,
  );

  const conversionRateValue =
    casinoConversionRate?.data?.casino_conversion_rate;

  const handleGameClick = useCallback(
    (game) => {
      if (game?.path) {
        if (conversionRateValue === 1) {
          navigate(game.path);
        } else {
          setPathUrl(game.path);
          setDismissedConversionRateDialog(true);
        }
      } else {
        navigateToCasinoGameLaunch(navigate, game, {}, auth);
      }
    },
    [navigate, auth],
  );

  const onCasinoPathClick = useCallback(
    (e) => {
      const isDemo =
        auth?.user?.is_demo || auth?.user?.isDemo || auth?.isDemoLogin;
      if (!auth.token) {
        e.preventDefault();
        if (typeof window.openLoginDialog === "function") {
          window.openLoginDialog();
        } else {
          toast.error("Please login to visit Casino.");
        }
      } else if (isDemo) {
        e.preventDefault();
        toast.error("Demo users are not allowed to play casino games.");
      }
    },
    [auth],
  );

  const activeSport = useSelector((state) => state?.sidebar?.sportsInfo);

  const isSportAvailable = (id) => {
    return activeSport?.some((sport) => sport.sport_id === id);
  };

  return (
    <section className="section listing_page mt-20">
      <Modal
        show={dismissedConversionRateDialog}
        onHide={() => setDismissedConversionRateDialog(false)}
        centered
        className="engcasinomodalbgimg_main"
      >
        <div className="engcasinomodalbgimg">
          <button
            type="button"
            onClick={() => setDismissedConversionRateDialog(false)}
            className="close"
            aria-label="Close"
          >
            ×
          </button>

          <img alt="" className="img-fluid" src={casinoPoint100} />
          <h2 className="lobby">1 POINT = {conversionRateValue}</h2>
          <p className="maximum_bet">2,00,000</p>
          <button
            type="button"
            className="virtual-close-btn"
            onClick={() => {
              navigate(pathUrl);
              setDismissedConversionRateDialog(false);
            }}
          >
            &nbsp;
          </button>
        </div>
      </Modal>
      <div className="row">
        <div className="col-12 d-flex">
          <div className="commentary">
            <img src={commentary} alt="commentary" />

            <marquee>{newsHeadingsText || "Welcome..."}</marquee>
          </div>
        </div>
      </div>
      <div className="col-lg-12 listing_page_12">
        <div className="middle-page-content home-page">
          <div className="game-slider mb-2">
            <div className="coupon-card">
              <div className="custom-thumb-section">
                {(isSportAvailable("QT") ? THUMB_IMAGES_QT : THUMB_IMAGES).map(
                  (item, index) => (
                    <div
                      key={item.path || item.game_id || item.game_code || index}
                      className={`cts-img popularDiv${item.hcDiv ? " hc_div" : ""}`}
                    >
                      {thumbItemHasPath(item) ? (
                        <Link to={item.path} onClick={onCasinoPathClick}>
                          <img
                            className="img-fluid"
                            src={item.src}
                            alt={item.game_name || ""}
                          />
                        </Link>
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleGameClick(item)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleGameClick(item)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            className="img-fluid"
                            src={item.src}
                            alt={item.game_name || ""}
                          />
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
              <div className="col-12 px-0 d-block d-md-none">
                <h2 className="ipl">new launch</h2>
              </div>
              <div className="top-baner mySwiper d-block d-md-none">
                <Swiper
                  modules={[Pagination]}
                  className="slider_banner"
                  spaceBetween={2}
                  slidesPerView={3}
                  loop
                  pagination={false}
                  breakpoints={{
                    320: { slidesPerView: 3 },
                    576: { slidesPerView: 4 },
                  }}
                >
                  {(isSportAvailable("QT")
                    ? BANNER_SLIDES_QT
                    : BANNER_SLIDES
                  ).map((game, index) => (
                    <SwiperSlide
                      key={game.gameId ?? index}
                      className="slider_banner__item"
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleGameClick(game)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleGameClick(game)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          alt={game.alt || game.gameName || ""}
                          className="img-fluid slider_banner__item_img"
                          src={game.src}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              {hasAnyTodayMatches && (
                <div className="card">
                  {hasCricket && (
                    <div className="card-body card-content p-0">
                      <MatchCard
                        sportTitle="Cricket"
                        matches={cricketMatches}
                        sportIcon={cricket}
                        instanceId="today"
                      />
                    </div>
                  )}
                  {hasFootball && (
                    <div className="card-body card-content p-0">
                      <MatchCard
                        sportTitle="Football"
                        matches={footballMatches}
                        sportIcon={football}
                        instanceId="today"
                      />
                    </div>
                  )}
                  {hasTennis && (
                    <div className="card-body card-content p-0">
                      <MatchCard
                        sportTitle="Tennis"
                        matches={tennisMatches}
                        sportIcon={tennis}
                        instanceId="today"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <span className="list-sport-title">
              <img
                src={casino}
                className="img-fluid game-icon-img"
                alt="casino"
              />{" "}
              Casino Provider
            </span>
            <div className="casinoprovider-thumb-section">
              {(isSportAvailable("QT")
                ? CASINO_PROVIDER_IMAGES_QT
                : CASINO_PROVIDER_IMAGES
              ).map((item, index) => (
                <div key={index} className="cts-img popularDiv">
                  <Link to={item.path || "/casino"} onClick={onCasinoPathClick}>
                    <img className="img-fluid" alt="" src={item.src} />
                  </Link>
                </div>
              ))}
            </div>
            {hasAnyUpcomingMatches && (
              <div className="card">
                <span className="list-sport-title upcomingmatch">
                  <img
                    src={upcomingEventsIcon}
                    className="img-fluid game-icon-img"
                    alt="upcoming events"
                  />{" "}
                  Upcoming Events
                </span>
                {hasUpcomingCricket && (
                  <div className="card-body card-content p-0">
                    <MatchCard
                      sportTitle="Cricket"
                      matches={upcomingCricketMatches}
                      sportIcon={cricket}
                      instanceId="upcoming"
                    />
                  </div>
                )}
                {hasUpcomingFootball && (
                  <div className="card-body card-content p-0">
                    <MatchCard
                      sportTitle="Football"
                      matches={upcomingFootballMatches}
                      sportIcon={football}
                      instanceId="upcoming"
                    />
                  </div>
                )}
                {hasUpcomingTennis && (
                  <div className="card-body card-content p-0">
                    <MatchCard
                      sportTitle="Tennis"
                      matches={upcomingTennisMatches}
                      sportIcon={tennis}
                      instanceId="upcoming"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;
